import React from 'react';
import { Download, FileText, Image, Film, File, Calculator } from 'lucide-react';
import { useSalesData } from '../context/SalesDataContext';
import { useCapabilities } from '../hooks/useCapabilities';

const WhatsAppAttachmentItem = ({
    att,
    extractedData = {},
    onDownload,
    onAnalyze,
    onCompare
}) => {
    // Safely handle contexts that might not be available (e.g., in backoffice)
    let priceData = null;
    let hasCapability = () => false;
    
    try {
        const salesData = useSalesData();
        priceData = salesData?.priceData || null;
    } catch (e) {
        // Context not available (e.g., in backoffice)
    }
    
    try {
        const capabilities = useCapabilities();
        hasCapability = capabilities?.hasCapability || (() => false);
    } catch (e) {
        // Context not available
    }
    
    const hasVentasElectricidad = hasCapability('ventas_electricidad');
    const hasImaginaPrice = priceData?.imagina && Object.keys(priceData.imagina).length > 0;

    const getFileIcon = (mimeType) => {
        if (!mimeType) return <File size={16} className="text-slate-400" />;
        if (mimeType === 'application/pdf') return <FileText size={16} className="text-red-400" />;
        if (mimeType.startsWith('image/')) return <Image size={16} className="text-blue-400" />;
        if (mimeType.startsWith('video/')) return <Film size={16} className="text-purple-400" />;
        return <File size={16} className="text-slate-400" />;
    };

    const formatSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    if (!att) return null;

    return (
        <div className="bg-slate-800/60 p-3 rounded-lg border border-slate-700/50 hover:border-emerald-500/30 transition-all group my-1 w-[280px]">
            <div className="flex items-start gap-2.5">
                <div className="mt-0.5 flex-shrink-0">
                    {getFileIcon(att.mime_type)}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-200 font-medium truncate" title={att.filename || att.original_filename || 'Adjunto'}>
                        {att.filename || att.original_filename || 'Adjunto'}
                    </p>
                    <p className="text-[9px] text-slate-500 mt-0.5">
                        {att.mime_type || 'Multimedia'}
                        {att.size ? ` · ${formatSize(att.size)}` : ''}
                    </p>

                    {extractedData[att.id] && (() => {
                        const rawEx = extractedData[att.id];
                        const ex = rawEx.data ? rawEx.data : rawEx;
                        
                        const consumoData = ex.consumo || {};
                        const totalesData = ex.totales || {};
                        const potenciaData = ex.potencia || ex.potencias || {};
                        
                        const cae = consumoData.consumo_anual_estimado_kwh || consumoData.consumo_anual_kwh || consumoData.cae || consumoData.consumo_total_kWh || 0;
                        const subEnergia = totalesData['total_energia_€'] || 0;
                        const subPotencia = totalesData['total_potencia_€'] || 0;

                        return (
                            <div className="mt-2.5 p-2 bg-emerald-500/5 rounded-lg border border-emerald-500/20 space-y-2 animate-in fade-in slide-in-from-top-1 duration-300">
                                {/* Main Stats */}
                                <div className="flex items-center justify-between text-[10px]">
                                    <div className="flex flex-col">
                                        <span className="text-slate-500 text-[8px] uppercase font-bold tracking-tighter">Consumo (CAE)</span>
                                        <span className="text-emerald-400 font-black">{cae.toLocaleString()} <small className="font-normal opacity-70">kWh</small></span>
                                    </div>
                                    <div className="flex gap-4 text-right">
                                        <div className="flex flex-col">
                                            <span className="text-slate-500 text-[8px] uppercase font-bold tracking-tighter">S. Energía</span>
                                            <span className="text-emerald-400 font-black">
                                                {subEnergia.toFixed(2)} <small className="font-normal opacity-70">€</small>
                                            </span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-slate-500 text-[8px] uppercase font-bold tracking-tighter">S. Potencia</span>
                                            <span className="text-emerald-400 font-black">
                                                {subPotencia.toFixed(2)} <small className="font-normal opacity-70">€</small>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Powers P1-P6 Grid */}
                                <div className="pt-2 border-t border-emerald-500/10">
                                    <span className="text-slate-500 text-[7px] uppercase font-black tracking-widest block mb-1.5">Potencias Contratadas (kW)</span>
                                    <div className="grid grid-cols-6 gap-1">
                                        {['p1', 'p2', 'p3', 'p4', 'p5', 'p6'].map(p => {
                                            const pVal = potenciaData[`potencia_${p}_kw`] || potenciaData[`potencia_${p}`] || 0;
                                            return (
                                                <div key={p} className="flex flex-col items-center bg-slate-900/60 rounded py-1 border border-slate-800">
                                                    <span className="text-[7px] text-slate-500 uppercase font-bold">{p}</span>
                                                    <span className="text-[9px] text-slate-300 font-black">
                                                        {pVal}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Actions */}
            <div className="flex gap-1.5 mt-2.5 w-full">
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onDownload) onDownload(att);
                    }}
                    className="flex-1 min-w-0 flex items-center justify-center py-1.5 px-1 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 hover:text-white rounded-lg transition-colors cursor-pointer"
                    title="Descargar"
                >
                    <Download size={14} />
                </button>
                
                {att.mime_type === 'application/pdf' && (
                    <>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onAnalyze) onAnalyze(att);
                            }}
                            disabled={!hasVentasElectricidad}
                            className={`flex-1 min-w-0 flex items-center justify-center py-1.5 px-1 rounded-lg border transition-all group/btn ${
                                hasVentasElectricidad
                                ? 'bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 hover:text-emerald-100 border-emerald-500/20 cursor-pointer'
                                : 'bg-slate-700/30 border-slate-600/30 text-slate-500 opacity-50 grayscale cursor-not-allowed'
                            }`}
                            title={hasVentasElectricidad ? "Analizar" : "No tienes permiso para analizar facturas eléctricas"}
                        >
                            <FileText size={14} className={hasVentasElectricidad ? "group-hover/btn:scale-110 transition-transform" : ""} />
                        </button>
                        {extractedData[att.id] && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onCompare) onCompare(att);
                                }}
                                disabled={!hasImaginaPrice}
                                className={`flex-1 min-w-0 flex items-center justify-center py-1.5 px-1 rounded-lg border transition-all animate-in zoom-in duration-300 group/btn ${
                                    hasImaginaPrice 
                                    ? 'bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 hover:text-blue-100 border-blue-500/30 cursor-pointer'
                                    : 'bg-slate-700/30 border-slate-600/30 text-slate-500 cursor-not-allowed'
                                }`}
                                title={hasImaginaPrice ? "Ver comparativa" : "Calcula el precio de Imagina primero"}
                            >
                                <Calculator size={14} className="group-hover/btn:rotate-12 transition-transform" />
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default WhatsAppAttachmentItem;
